import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import Paging from "../../component/Paging";
import request from '../../../utils/Request.utils';
import { ToastContainer, toast } from 'react-toastify';
import Modal from '../../component/Modal';

const MemberManage = ({ isLoggedIn }) => {
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currPage, setCurrPage] = useState(1);
  const [totalCnt, setTotalCnt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // 검색
  const [searchQuery, setSearchQuery] = useState('');
  const [inputQuery, setInputQuery] = useState(''); // 입력 필드 상태
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');

  const successHandler = useCallback((response) => {
    if (response.messageCode === 200) {
      setUsers(response.data.dataList);
      setTotalPages(response.data.tot_page);
      setTotalCnt(response.data.totalCnt);
      setLoading(false);
    } else {
      toast.error("회원 정보를 불러올 수 없습니다.");
      setLoading(false);
    }
  }, []);

  const errorHandler = useCallback((error) => {
    toast.error("데이터를 불러오는 중 오류가 발생했습니다.");
    setLoading(false);
  }, []);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = {
      curr_page: currPage,
      per_page: 10,
      sort_col: 'reg_dtm',
      sort_by: 'desc',
      user_stat: 'all',
      search_query: searchQuery,
      search_start_date: searchStartDate,
      search_end_date: searchEndDate,
    };
    request.get("/user/list", params, successHandler, errorHandler);
  }, [currPage, searchQuery, searchStartDate, searchEndDate, successHandler, errorHandler]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = (page) => {
    setCurrPage(page);
  };

  const handleSearch = () => {
    setSearchQuery(inputQuery);
  };

  const handleReset = () => {
    setInputQuery('');
    setSearchQuery('');
    setSearchStartDate('');
    setSearchEndDate('');
    setCurrPage(1);
    fetchUsers();
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prevSelectedUsers =>
      prevSelectedUsers.includes(userId)
        ? prevSelectedUsers.filter(id => id !== userId)
        : [...prevSelectedUsers, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.user_seq));
    }
  };

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);

  const handleDelete = async () => {
    try {
      await request.del("/user/delete", { user_seqs: selectedUsers }, () => {
        toast.success("회원이 성공적으로 삭제되었습니다.");
        fetchUsers();
        setSelectedUsers([]);
      }, () => {
        toast.error("회원 삭제 중 오류가 발생했습니다.");
      });
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  return (
    <main>
      <h2 className="adm_com_pg_tit">회원 관리</h2>
      <div className="adm_com_pg_content">
        <div className="adm_com_board_list__top">
          <div className="adm_com_board_search">
            <div className="adm_com_input_wrap flex_row">
              <span className="tit">가입일 : </span>
              <input
                type="date"
                className="adm_com_datepicker"
                value={searchStartDate}
                onChange={(e) => setSearchStartDate(e.target.value)}
              />
              <span className="divider"> ~ </span>
              <input
                type="date"
                className="adm_com_datepicker"
                value={searchEndDate}
                onChange={(e) => setSearchEndDate(e.target.value)}
              />
            </div>
            <article className="search end">
              <select className="select">
                <option value="전체">전체</option>
              </select>
              <input
                type="search"
                value={inputQuery}
                onChange={(e) => setInputQuery(e.target.value)}
                placeholder="검색할 내용을 입력해 주세요."
                className="input ws type_search"
              />
              <button className="adm_com_btn blue" onClick={handleSearch}>조회</button>
              <button className="adm_com_btn gray" onClick={handleReset}>초기화</button>
            </article>
          </div>
        </div>
        <div className="adm_com_btn_wrap">
          <button className="adm_com_btn gray" onClick={openModal}>선택삭제</button>
        </div>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <table className="adm_com_board_list">
            <colgroup>
              <col width="5%" />
              <col width="5%" />
              <col width="10%" />
              <col width="15%" />
              <col width="20%" />
              <col width="10%" />
              <col width="10%" />
              <col width="10%" />
              <col width="5%" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div className="check">
                    <input
                      type="checkbox"
                      id="chk01"
                      className="adm_com_chk"
                      onChange={handleSelectAll}
                      checked={selectedUsers.length === users.length}
                    />
                    <label htmlFor="chk01" />
                  </div>
                </th>
                <th>번호</th>
                <th>회원번호</th>
                <th>아이디</th>
                <th>휴대폰</th>
                <th>이메일</th>
                <th>가입일</th>
                <th>최근접속일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.user_seq}>
                  <td>
                    <div className="check">
                      <input
                        type="checkbox"
                        id={`chk${index + 2}`}
                        className="adm_com_chk"
                        checked={selectedUsers.includes(user.user_seq)}
                        onChange={() => handleSelectUser(user.user_seq)}
                      />
                      <label htmlFor={`chk${index + 2}`} />
                    </div>
                  </td>
                  <td>{totalCnt - ((currPage - 1) * 10 + index)}</td>
                  <td>{user.user_seq}</td>
                  <td>{user.user_id}</td>
                  <td>{user.user_phone}</td>
                  <td>{user.user_email}</td>
                  <td>{new Date(user.reg_dtm).toLocaleDateString()}</td>
                  <td>{new Date(user.last_login_dt).toLocaleDateString()}</td>
                  <td>
                    <NavLink
                      to={`/Member/MemberManage/Detail/${user.user_seq}`}
                      className="adm_com_btn blue line"
                    >
                      상세
                    </NavLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paging
          currentPage={currPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
      <Modal
        open={modalIsOpen}
        close={closeModal}
        type="confirm"
        popSize="s"
        title="삭제 확인"
        description="선택된 회원을 삭제하시겠습니까?"
      >
        <div className="btn_container">
          <button className="adm_com_btn blue" onClick={handleDelete}>삭제</button>
          <button className="adm_com_btn gray" onClick={closeModal}>취소</button>
        </div>
      </Modal>
      <ToastContainer />
    </main>
  );
};

export default MemberManage;
